import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { useAuth } from "@/hooks/useAuth";
import { ingestSummary } from "@/components/research-hub/labels";

type SourceType =
  | "manual_text" | "drive_pdf" | "drive_doc" | "gmail_message"
  | "slidedeck" | "web_page" | "external_link";

const FILE_TYPE_HINT: Record<string, SourceType> = {
  pdf: "drive_pdf",
  docx: "drive_doc", doc: "drive_doc", txt: "manual_text", md: "manual_text",
  eml: "gmail_message", msg: "gmail_message",
  pptx: "slidedeck", ppt: "slidedeck",
};

async function createSource(row: any, userId: string | undefined) {
  const { data, error } = await supabase
    .from("research_sources")
    .insert({ ...row, ingested_by: userId ?? null })
    .select("id")
    .single();
  if (error) throw error;
  return data.id as string;
}

async function triggerPipeline(sourceId: string) {
  // Best-effort: extract -> kg-extract. Fire-and-forget, don't block UI.
  await supabase.functions.invoke("research-triage-source", { body: { source_id: sourceId } }).catch(() => {});
  await supabase.functions.invoke("research-extract-source", { body: { source_id: sourceId } }).catch(() => {});
  await supabase.functions.invoke("research-kg-extract", { body: { source_id: sourceId } }).catch(() => {});
}

export default function ResearchIngest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: canWrite, isLoading } = useResearchCapability("author");
  const [busy, setBusy] = useState(false);

  // Paste-text form
  const [pTitle, setPTitle] = useState("");
  const [pBody, setPBody] = useState("");
  const [pAuthors, setPAuthors] = useState("");
  const [pUrl, setPUrl] = useState("");
  const [pType, setPType] = useState<SourceType>("manual_text");

  // URL fetch form
  const [uUrl, setUUrl] = useState("");
  const [uTitle, setUTitle] = useState("");

  // File upload form
  const [fTitle, setFTitle] = useState("");
  const [fAuthors, setFAuthors] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fForce, setFForce] = useState(false);

  // URL fetch extras
  const [uForce, setUForce] = useState(false);

  // Paste text extras
  const [tForce, setTForce] = useState(false);

  // Gmail ID form
  const [gIds, setGIds] = useState("");
  const [gResults, setGResults] = useState<any[] | null>(null);
  const [gForce, setGForce] = useState(false);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canWrite) return <p className="text-sm text-muted-foreground">Requires research write capability.</p>;

  const submitText = async () => {
    if (!pTitle.trim() || !pBody.trim()) { toast.error("Title and body required"); return; }
    setBusy(true);
    try {
      const id = await createSource({
        source_type: pType,
        title: pTitle.trim(),
        external_url: pUrl.trim() || null,
        authors: pAuthors ? pAuthors.split(",").map((s) => s.trim()).filter(Boolean) : null,
        extracted_text: pBody,
        word_count: pBody.trim().split(/\s+/).length,
        status: "extracted",
        raw_metadata: { ingested_via: "manual_paste", force: tForce || undefined },
      }, user?.id);
      toast.success("Source created — running KG extraction in background");
      triggerPipeline(id);
      navigate(`/partner/research/kg`);
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const submitUrl = async () => {
    if (!uUrl.trim()) { toast.error("URL required"); return; }
    setBusy(true);
    try {
      const id = await createSource({
        source_type: "web_page",
        title: uTitle.trim() || uUrl.trim(),
        external_url: uUrl.trim(),
        status: "queued",
        raw_metadata: { ingested_via: "manual_url", force: uForce || undefined },
      }, user?.id);
      // Web ingester will fetch + extract + chunk.
      await supabase.functions.invoke("research-ingest-web", {
        body: { source_id: id, url: uUrl.trim(), force: uForce },
      }).catch(() => {});
      triggerPipeline(id);
      toast.success("URL queued — fetch + KG extraction running");
      navigate(`/partner/research/kg`);
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const submitFile = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    setBusy(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const guessed = FILE_TYPE_HINT[ext] ?? "manual_text";
      const path = `manual/${user?.id ?? "anon"}/${Date.now()}-${file.name.replace(/[^\w.\-]+/g, "_")}`;
      const up = await supabase.storage.from("research-uploads").upload(path, file, {
        contentType: file.type || undefined, upsert: false,
      });
      if (up.error) throw up.error;
      const id = await createSource({
        source_type: guessed,
        title: fTitle.trim() || file.name,
        authors: fAuthors ? fAuthors.split(",").map((s) => s.trim()).filter(Boolean) : null,
        storage_path: path,
        status: "queued",
        raw_metadata: {
          ingested_via: "manual_upload",
          original_filename: file.name,
          mime_type: file.type,
          size_bytes: file.size,
          force: fForce || undefined,
        },
      }, user?.id);
      // Document extractor handles PDF/DOCX text extraction; then triage + kg.
      await supabase.functions.invoke("research-extract-document", { body: { source_id: id } }).catch(() => {});
      triggerPipeline(id);
      toast.success("File uploaded — extraction + KG running in background");
      navigate(`/partner/research/kg`);
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const submitGmail = async () => {
    const ids = gIds.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) { toast.error("Paste at least one Gmail ID"); return; }
    setBusy(true);
    setGResults(null);
    try {
      const { data, error } = await supabase.functions.invoke("research-ingest-gmail", { body: { ids, force: gForce } });
      if (error) throw error;
      const results = (data as any)?.results ?? [];
      setGResults(results);
      const ok = results.filter((r: any) => r.source_id && !r.deduped).length;
      const dup = results.filter((r: any) => r.deduped).length;
      const err = results.filter((r: any) => r.error).length;
      toast.success(`Gmail ingest: ${ok} new, ${dup} duplicate, ${err} error`);
      for (const r of results) {
        if (r.source_id && !r.deduped) triggerPipeline(r.source_id);
      }
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Manual ingest</h2>
        <p className="text-sm text-muted-foreground">
          Add a source by pasting text, uploading a file (PDF / DOCX / EML / PPTX), or fetching a URL.
          Each submission creates a <code>research_sources</code> row and kicks off triage,
          extraction and knowledge-graph fact extraction.
        </p>
      </div>

      <Tabs defaultValue="text">
        <TabsList>
          <TabsTrigger value="text">Paste text / email body</TabsTrigger>
          <TabsTrigger value="gmail">Gmail ID</TabsTrigger>
          <TabsTrigger value="file">Upload file</TabsTrigger>
          <TabsTrigger value="url">Fetch URL</TabsTrigger>
        </TabsList>

        <TabsContent value="text">
          <Card>
            <CardHeader><CardTitle>Paste content</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2"><Label>Title</Label><Input value={pTitle} onChange={(e) => setPTitle(e.target.value)} maxLength={300} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2"><Label>Authors (comma separated)</Label><Input value={pAuthors} onChange={(e) => setPAuthors(e.target.value)} placeholder="Jane Doe, Acme Capital" maxLength={500} /></div>
                <div className="grid gap-2"><Label>Source URL (optional)</Label><Input value={pUrl} onChange={(e) => setPUrl(e.target.value)} placeholder="https://…" maxLength={1000} /></div>
              </div>
              <div className="grid gap-2">
                <Label>Source type</Label>
                <select className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                        value={pType} onChange={(e) => setPType(e.target.value as SourceType)}>
                  <option value="manual_text">manual_text</option>
                  <option value="gmail_message">gmail_message</option>
                  <option value="external_link">external_link</option>
                  <option value="web_page">web_page</option>
                </select>
              </div>
              <div className="grid gap-2"><Label>Body</Label>
                <Textarea value={pBody} onChange={(e) => setPBody(e.target.value)} rows={14} maxLength={200000} placeholder="Paste the email body, note, or any text…" />
                <div className="text-xs text-muted-foreground">{pBody.length.toLocaleString()} chars</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tForce} onChange={(e) => setTForce(e.target.checked)} />
                Force re-ingest (ignore duplicate check)
              </label>
              <Button onClick={submitText} disabled={busy}>{busy ? "Ingesting…" : "Ingest"}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gmail">
          <Card>
            <CardHeader><CardTitle>Ingest by Gmail ID</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label>Gmail thread/message IDs or RFC822 Message-IDs</Label>
                <Textarea
                  value={gIds}
                  onChange={(e) => setGIds(e.target.value)}
                  rows={6}
                  placeholder={"One per line. Accepts:\n  • Hex Gmail API id (e.g. 18c2a1b3c4d5e6f7)\n  • RFC822 Message-ID (e.g. <CAB...@mail.gmail.com>)\n  • Full Gmail URL containing a hex id"}
                  maxLength={20000}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={gForce} onChange={(e) => setGForce(e.target.checked)} />
                Force re-ingest (ignore duplicate check)
              </label>
              <Button onClick={submitGmail} disabled={busy}>{busy ? "Fetching from Gmail…" : "Ingest from Gmail"}</Button>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Uses the Google account you linked at <code>/partner/research/connectors</code>.</p>
                <p><strong>Note:</strong> Gmail UI permalinks like <code>FMfcgz…</code> are <em>not</em> usable via the API. To get a working ID: open the email in Gmail → <em>⋮ → Show original</em> → copy the <code>Message-ID</code> header value and paste it here.</p>
              </div>
              {gResults && (
                <div className="border rounded-md divide-y text-xs">
                  {gResults.map((r, i) => (
                    <div key={i} className="p-2 flex items-start justify-between gap-2">
                      <code className="break-all">{r.input}</code>
                      {r.error ? (
                        <span className="text-destructive">{r.error}</span>
                      ) : r.deduped ? (
                        <span className="text-muted-foreground">already ingested</span>
                      ) : (
                        <span className="text-primary">
                          {r.source_id ? (
                            <Link
                              to={`/partner/research/sources?id=${r.source_id}`}
                              className="hover:underline"
                            >
                              {ingestSummary({
                                word_count: r.word_count,
                                title: r.title ?? r.subject,
                                source_type: "gmail_thread",
                                source_id: r.source_id,
                              })}
                            </Link>
                          ) : (
                            ingestSummary({
                              word_count: r.word_count,
                              title: r.title ?? r.subject,
                              source_type: "gmail_thread",
                            })
                          )}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="file">
          <Card>
            <CardHeader><CardTitle>Upload a file</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2"><Label>Title (defaults to filename)</Label><Input value={fTitle} onChange={(e) => setFTitle(e.target.value)} maxLength={300} /></div>
              <div className="grid gap-2"><Label>Authors (comma separated)</Label><Input value={fAuthors} onChange={(e) => setFAuthors(e.target.value)} maxLength={500} /></div>
              <div className="grid gap-2"><Label>File</Label>
                <Input type="file" accept=".pdf,.docx,.doc,.txt,.md,.eml,.msg,.pptx,.ppt"
                       onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                {file && <div className="text-xs text-muted-foreground">{file.name} — {(file.size / 1024).toFixed(1)} KB</div>}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={fForce} onChange={(e) => setFForce(e.target.checked)} />
                Force re-ingest (ignore duplicate check)
              </label>
              <Button onClick={submitFile} disabled={busy || !file}>{busy ? "Uploading…" : "Upload & ingest"}</Button>
              <p className="text-xs text-muted-foreground">
                PDFs and DOCX are routed through <code>research-extract-document</code> for text extraction,
                then through KG extraction. For an email, export from Gmail as <code>.eml</code> or paste the body in the previous tab.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="url">
          <Card>
            <CardHeader><CardTitle>Fetch a URL</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2"><Label>URL</Label><Input value={uUrl} onChange={(e) => setUUrl(e.target.value)} placeholder="https://…" maxLength={1000} /></div>
              <div className="grid gap-2"><Label>Title (optional)</Label><Input value={uTitle} onChange={(e) => setUTitle(e.target.value)} maxLength={300} /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={uForce} onChange={(e) => setUForce(e.target.checked)} />
                Force re-ingest (ignore duplicate check)
              </label>
              <Button onClick={submitUrl} disabled={busy}>{busy ? "Queuing…" : "Fetch & ingest"}</Button>
              <p className="text-xs text-muted-foreground">
                Public pages only. Pages behind login (Gmail, Bloomberg, FT) will fail — use the paste-text tab for those.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
